import React from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import styled from '@emotion/styled';

import { CircularProgressbar } from 'react-circular-progressbar';

const containerBaseStyles = ({ theme }) => css`
  color: ${theme.colors.white};
  position: relative;
`;

const containerSizeStyles = ({ size }) => {
  const sizeMap = {
    bit: {
      width: '36px',
      height: '36px',
    },
    byte: {
      width: '50px',
      height: '50px',
    },
    kilo: {
      width: '65px',
      height: '65px',
    },
    mega: {
      width: '103px',
      height: '103px',
    },
  };

  const sizeProps = sizeMap[size] || sizeMap.kilo;

  return css`
    ${sizeProps};
  `;
};

const Container = styled('div')(containerBaseStyles, containerSizeStyles);

const chartBaseStyles = () => css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
`;

const Chart = styled('div')(chartBaseStyles);

const chartContentBaseStyles = ({ theme }) => css`
  border-radius: ${theme.borderRadius.circle};
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  overflow: hidden;
`;

const ChartContent = styled('div')(chartContentBaseStyles);

const CirclePercentage = ({
  children,
  size,
  percentage,
  strokeWidth,
  startColor,
  endColor,
  trailStrokeColor,
  gradientId,
}) => {
  const gradientTransform = `rotate(90)`;

  return (
    <Container size={size}>
      <svg style={{ height: 0, width: 0 }}>
        <defs>
          <linearGradient id={gradientId} gradientTransform={gradientTransform}>
            <stop offset='2%' stopColor={startColor} />
            <stop offset='47%' stopColor={endColor} />
          </linearGradient>
        </defs>
      </svg>

      <Chart>
        <CircularProgressbar
          value={percentage}
          strokeWidth={strokeWidth}
          styles={{
            path: {
              stroke: `url(#${gradientId})`,
              height: '100%',
              strokeLinecap: 'round',
            },
            trail: {
              strokeLinecap: 'round',
              stroke: trailStrokeColor,
            },
          }}
        />
      </Chart>

      <ChartContent>{children}</ChartContent>
    </Container>
  );
};

CirclePercentage.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  size: PropTypes.string,
  percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  strokeWidth: PropTypes.number,
  startColor: PropTypes.string,
  endColor: PropTypes.string,
  trailStrokeColor: PropTypes.string,
  gradientId: PropTypes.string,
  chartTheme: PropTypes.object,
};

CirclePercentage.defaultProps = {
  gradientId: 'circleChart',
  strokeWidth: 6,
  trailStrokeColor: '#454545',
  startColor: '#FF8119',
  endColor: '#D14D09',
};

export default CirclePercentage;
